// src/adminComponents/BottomNav.js
import React, { useRef, useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";

const AdminBottomNav = () => {
  const location = useLocation();
  const navContainerRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ transform: "translate3d(0, 0, 0)", opacity: 0 });
  const [isNavHidden, setIsNavHidden] = useState(false);
  const lastScrollY = useRef(0);
  const isNavHiddenRef = useRef(false);

  // Auto-hide on scroll down (swipe up), auto-show on scroll up (swipe down)
  useEffect(() => {
    let rafId = null;
    const SCROLL_THRESHOLD = 8;
    const TOP_THRESHOLD = 15;
    let touchStartY = 0;
    let touchStartX = 0;

    const getScrollTop = () => {
      return (
        window.pageYOffset ||
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      );
    };

    const updateNavVisibility = (hidden) => {
      if (isNavHiddenRef.current !== hidden) {
        isNavHiddenRef.current = hidden;
        setIsNavHidden(hidden);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(() => {
        const currentScrollY = getScrollTop();
        const delta = currentScrollY - lastScrollY.current;

        if (currentScrollY <= TOP_THRESHOLD) {
          updateNavVisibility(false);
        } else if (delta > SCROLL_THRESHOLD) {
          updateNavVisibility(true);
        } else if (delta < -SCROLL_THRESHOLD) {
          updateNavVisibility(false);
        }

        lastScrollY.current = currentScrollY <= 0 ? 0 : currentScrollY;
        rafId = null;
      });
    };

    const handleTouchStart = (e) => {
      if (e.touches && e.touches[0]) {
        touchStartY = e.touches[0].clientY;
        touchStartX = e.touches[0].clientX;
      }
    };

    const handleTouchMove = (e) => {
      if (!e.touches || !e.touches[0]) return;
      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const deltaY = touchStartY - currentY;
      const deltaX = Math.abs(currentX - touchStartX);

      if (Math.abs(deltaY) > deltaX) {
        const currentScrollY = getScrollTop();
        if (currentScrollY <= TOP_THRESHOLD && deltaY < 0) {
          updateNavVisibility(false);
        } else if (deltaY > 18) {
          updateNavVisibility(true);
        } else if (deltaY < -18) {
          updateNavVisibility(false);
        }
      }
    };

    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > 3) {
        const currentScrollY = getScrollTop();
        if (currentScrollY <= TOP_THRESHOLD && e.deltaY < 0) {
          updateNavVisibility(false);
        } else if (e.deltaY > 0) {
          updateNavVisibility(true);
        } else if (e.deltaY < 0) {
          updateNavVisibility(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // When changing routes/pages, ensure the bottom nav is visible
  useEffect(() => {
    isNavHiddenRef.current = false;
    setIsNavHidden(false);
    lastScrollY.current = 0;
  }, [location.pathname]);

  const navItems = [
    { to: "/admin/dashboard", label: "Dashboard", icon: "fi fi-rr-apps" },
    { to: "/admin/contactus", label: "Support", icon: "fi fi-rr-headset" },
    { to: "/admin/userstats", label: "User Stats", icon: "fi fi-rr-chart-pie-alt" },
    { to: "/", label: "App", icon: "fi fi-rr-arrow-right-from-bracket" },
  ];

  useEffect(() => {
    if (!navContainerRef.current) return;
    const activeIndex = navItems.findIndex((item) => {
      if (item.to === "/") return false;
      return location.pathname === item.to;
    });

    if (activeIndex !== -1) {
      const activeElement = navContainerRef.current.children[activeIndex + 1]; // +1 for indicator element
      if (activeElement) {
        const left = activeElement.offsetLeft + activeElement.offsetWidth / 2 - 17;
        setIndicatorStyle({
          transform: `translate3d(${left}px, 0, 0)`,
          opacity: 1,
        });
      }
    } else {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
    }
  }, [location.pathname]);

  return (
    <div className={`floating-bottom-nav ${isNavHidden ? "nav-hidden" : ""}`}>
      <div className="nav-items-container" ref={navContainerRef}>
        <div className="bottom-nav-indicator" style={indicatorStyle} />

        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/admin/dashboard"}
            className={({ isActive }) => `nav-item ${isActive && item.to !== "/" ? "is-active" : ""}`}
          >
            <i className={item.icon} style={{ fontSize: "19px", lineHeight: "1" }}></i>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
};

export default AdminBottomNav;
